import React from 'react';
import { Shield, Eye, Lock, Globe, Users, Database, AlertTriangle } from 'lucide-react';
import { PageLayout } from '../components/layout/PageLayout';
import { Card } from '../components/common/Card';

export const PrivacyPage: React.FC = () => {
  return (
    <PageLayout
      title="Privacy Policy"
      subtitle="Last updated: August 17, 2025"
    >
      <div className="space-y-8 text-slate-700 dark:text-slate-300">
        
        {/* Quick Overview */}
        <Card className="p-6 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <div className="flex items-start space-x-4">
            <Shield className="h-8 w-8 text-blue-600 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
                Your Privacy Matters
              </h3>
              <p className="text-blue-800 dark:text-blue-200">
                We are committed to protecting your privacy and being transparent about how we collect, use, and protect your personal information. This policy complies with GDPR, CCPA, and other privacy regulations.
              </p>
            </div>
          </div>
        </Card>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">1. Information We Collect</h2>
          
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-3">1.1 Information You Provide</h3>
          <ul className="list-disc list-inside space-y-2 mb-4">
            <li><strong>Account Information:</strong> Email address, name (optional), profile preferences</li>
            <li><strong>Sleep Data:</strong> Sleep preferences, target sleep times, chronotype information</li>
            <li><strong>Usage Preferences:</strong> App settings, notification preferences, feature selections</li>
            <li><strong>Payment Information:</strong> Processed securely through PayPal (we do not store payment details)</li>
            <li><strong>Communications:</strong> Messages you send to customer support</li>
          </ul>

          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-3">1.2 Information Automatically Collected</h3>
          <ul className="list-disc list-inside space-y-2 mb-4">
            <li><strong>Usage Analytics:</strong> How you interact with our app, features used, session duration</li>
            <li><strong>Device Information:</strong> Device type, operating system, browser type, app version</li>
            <li><strong>Performance Data:</strong> App performance metrics, error reports, crash logs</li>
            <li><strong>Location Data:</strong> Approximate location for timezone and circadian rhythm calculations (optional)</li>
          </ul>

          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-3">1.3 Information We Don't Collect</h3>
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <p>We do <strong>NOT</strong> collect or have access to:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Your actual sleep patterns from wearable devices</li>
              <li>Medical or health records</li>
              <li>Precise location data</li>
              <li>Content from other apps or services</li>
              <li>Microphone or camera data</li>
            </ul>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">2. How We Use Your Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <Card className="p-4">
              <div className="flex items-center space-x-3 mb-3">
                <Eye className="h-6 w-6 text-purple-600" />
                <h3 className="font-semibold text-slate-900 dark:text-white">Service Provision</h3>
              </div>
              <ul className="text-sm space-y-1">
                <li>• Personalize sleep recommendations</li>
                <li>• Calculate optimal sleep schedules</li>
                <li>• Provide AI-powered insights</li>
                <li>• Sync preferences across devices</li>
              </ul>
            </Card>

            <Card className="p-4">
              <div className="flex items-center space-x-3 mb-3">
                <Database className="h-6 w-6 text-blue-600" />
                <h3 className="font-semibold text-slate-900 dark:text-white">Service Improvement</h3>
              </div>
              <ul className="text-sm space-y-1">
                <li>• Analyze usage patterns</li>
                <li>• Improve app performance</li>
                <li>• Develop new features</li>
                <li>• Fix bugs and errors</li>
              </ul>
            </Card>

            <Card className="p-4">
              <div className="flex items-center space-x-3 mb-3">
                <Users className="h-6 w-6 text-green-600" />
                <h3 className="font-semibold text-slate-900 dark:text-white">Customer Support</h3>
              </div>
              <ul className="text-sm space-y-1">
                <li>• Respond to your inquiries</li>
                <li>• Troubleshoot technical issues</li>
                <li>• Process subscription changes</li>
                <li>• Send important updates</li>
              </ul>
            </Card>

            <Card className="p-4">
              <div className="flex items-center space-x-3 mb-3">
                <Globe className="h-6 w-6 text-orange-600" />
                <h3 className="font-semibold text-slate-900 dark:text-white">Legal Compliance</h3>
              </div>
              <ul className="text-sm space-y-1">
                <li>• Comply with legal obligations</li>
                <li>• Enforce our Terms of Service</li>
                <li>• Protect against fraud</li>
                <li>• Respond to legal requests</li>
              </ul>
            </Card>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">3. Legal Basis for Processing (GDPR)</h2>
          <p className="mb-4">Under GDPR, we process your personal data based on the following legal bases:</p>
          <ul className="list-disc list-inside space-y-2 mb-4">
            <li><strong>Consent:</strong> For optional features like marketing communications and advanced analytics</li>
            <li><strong>Contract Performance:</strong> To provide the SleepCalc Pro service you've subscribed to</li>
            <li><strong>Legitimate Interests:</strong> For service improvement, security, and fraud prevention</li>
            <li><strong>Legal Obligation:</strong> To comply with applicable laws and regulations</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">4. Information Sharing and Disclosure</h2>
          
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 mb-6">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-6 w-6 text-red-600 mt-1" />
              <div>
                <h3 className="font-semibold text-red-900 dark:text-red-100 mb-2">We Do NOT Sell Your Data</h3>
                <p className="text-red-800 dark:text-red-200">
                  We never sell, rent, or trade your personal information to third parties for marketing purposes.
                </p>
              </div>
            </div>
          </div>

          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-3">We may share information with:</h3>
          <ul className="list-disc list-inside space-y-2 mb-4">
            <li><strong>Service Providers:</strong> PayPal for payment processing, analytics providers, cloud hosting services</li>
            <li><strong>Legal Requirements:</strong> When required by law, court order, or legal process</li>
            <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or asset sale</li>
            <li><strong>Safety and Security:</strong> To protect our users, prevent fraud, or address security issues</li>
          </ul>

          <p className="text-sm bg-slate-100 dark:bg-slate-800 p-4 rounded-lg">
            <strong>Third-Party Services:</strong> We use minimal third-party services and ensure they meet high privacy standards. All service providers are bound by strict data protection agreements.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">5. Data Security</h2>
          <p className="mb-4">
            We implement industry-standard security measures to protect your personal information:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="flex items-start space-x-3">
              <Lock className="h-5 w-5 text-green-600 mt-1" />
              <div>
                <strong>Encryption:</strong> All data is encrypted in transit and at rest using AES-256 encryption
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Shield className="h-5 w-5 text-blue-600 mt-1" />
              <div>
                <strong>Access Controls:</strong> Strict access controls and authentication for our systems
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Database className="h-5 w-5 text-purple-600 mt-1" />
              <div>
                <strong>Data Minimization:</strong> We only collect and store data necessary for our services
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Eye className="h-5 w-5 text-orange-600 mt-1" />
              <div>
                <strong>Regular Audits:</strong> Regular security audits and vulnerability assessments
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">6. Your Privacy Rights</h2>
          
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-3">6.1 General Rights</h3>
          <ul className="list-disc list-inside space-y-2 mb-4">
            <li><strong>Access:</strong> Request a copy of your personal data</li>
            <li><strong>Correction:</strong> Update or correct inaccurate information</li>
            <li><strong>Deletion:</strong> Request deletion of your personal data</li>
            <li><strong>Portability:</strong> Export your data in a machine-readable format</li>
            <li><strong>Objection:</strong> Object to certain types of processing</li>
          </ul>

          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-3">6.2 GDPR Rights (EU Users)</h3>
          <ul className="list-disc list-inside space-y-2 mb-4">
            <li><strong>Right to Rectification:</strong> Correct inaccurate personal data</li>
            <li><strong>Right to Erasure:</strong> "Right to be forgotten" under certain circumstances</li>
            <li><strong>Right to Restrict Processing:</strong> Limit how we process your data</li>
            <li><strong>Right to Data Portability:</strong> Receive your data in a structured format</li>
            <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
            <li><strong>Rights Related to Automated Decision-Making:</strong> Protection against solely automated decisions</li>
          </ul>

          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-3">6.3 CCPA Rights (California Users)</h3>
          <ul className="list-disc list-inside space-y-2 mb-4">
            <li><strong>Right to Know:</strong> Information about data collection and use</li>
            <li><strong>Right to Delete:</strong> Request deletion of personal information</li>
            <li><strong>Right to Opt-Out:</strong> Opt-out of the sale of personal information (we don't sell data)</li>
            <li><strong>Right to Non-Discrimination:</strong> Equal service regardless of privacy choices</li>
          </ul>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p><strong>How to Exercise Your Rights:</strong></p>
            <p>Contact us at <strong>privacy@sleepcalc.pro</strong> or through your account settings. We will respond within 30 days (or as required by applicable law).</p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">7. Data Retention</h2>
          <p className="mb-4">We retain your personal information only as long as necessary for the purposes outlined in this policy:</p>
          <ul className="list-disc list-inside space-y-2 mb-4">
            <li><strong>Account Data:</strong> Until you delete your account or request deletion</li>
            <li><strong>Usage Analytics:</strong> Aggregated and anonymized after 24 months</li>
            <li><strong>Support Communications:</strong> 3 years for quality assurance</li>
            <li><strong>Payment Records:</strong> As required by law and PayPal's retention policies</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">8. International Data Transfers</h2>
          <p className="mb-4">
            Your information may be transferred to and processed in countries other than your own. We ensure adequate protection through:
          </p>
          <ul className="list-disc list-inside space-y-2 mb-4">
            <li>Standard Contractual Clauses approved by the European Commission</li>
            <li>Adequacy decisions where applicable</li>
            <li>Other appropriate safeguards as required by law</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">9. Children's Privacy</h2>
          <p className="mb-4">
            SleepCalc Pro is not intended for children under 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us to have it deleted.
          </p>
          <p>
            Users aged 13-17 must have parental consent to use our service.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">10. Cookies and Tracking</h2>
          <p className="mb-4">We use cookies and similar technologies for:</p>
          <ul className="list-disc list-inside space-y-2 mb-4">
            <li><strong>Essential Cookies:</strong> Required for basic app functionality</li>
            <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
            <li><strong>Analytics Cookies:</strong> Understand how you use our app (with your consent)</li>
          </ul>
          <p>You can control cookies through your browser settings or our cookie preferences center.</p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">11. Changes to This Policy</h2>
          <p className="mb-4">
            We may update this Privacy Policy from time to time. We will notify you of any material changes by:
          </p>
          <ul className="list-disc list-inside space-y-2 mb-4">
            <li>Sending an email to your registered email address</li>
            <li>Posting a notice in the app</li>
            <li>Updating the "Last updated" date at the top of this policy</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">12. Contact Information</h2>
          <p className="mb-4">
            If you have any questions about this Privacy Policy or our privacy practices, please contact us:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-4">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-2">General Privacy Inquiries</h3>
              <p><strong>Email:</strong> privacy@sleepcalc.pro</p>
              <p><strong>Response Time:</strong> Within 30 days</p>
            </Card>

            <Card className="p-4">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Data Protection Officer</h3>
              <p><strong>Email:</strong> dpo@sleepcalc.pro</p>
              <p><strong>For:</strong> GDPR-related inquiries</p>
            </Card>
          </div>

          <div className="mt-6 bg-slate-100 dark:bg-slate-800 rounded-lg p-4">
            <p><strong>Mailing Address:</strong></p>
            <p>SleepCalc Pro Privacy Team</p>
            <p>[Company Address]</p>
            <p>[City, State, ZIP Code]</p>
          </div>
        </section>

        <div className="border-t border-slate-200 dark:border-slate-700 pt-8 text-center text-sm text-slate-500 dark:text-slate-400">
          <p>
            This Privacy Policy is compliant with GDPR, CCPA, and other applicable privacy laws. We are committed to protecting your privacy and being transparent about our data practices.
          </p>
        </div>
      </div>
    </PageLayout>
  );
};